import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { supabase } from '../lib/supabase.js'

const NIVEAUX = [
  'CP', 'CE1', 'CE2', 'CM1', 'CM2',
  '6e', '5e', '4e', '3e', 'CAP',
  '2nde', '1ère', 'Terminale',
  'Supérieur', 'Formation continue', 'Non applicable',
]

const PROFILE_TYPES = [
  { value: 'enseignant-formateur', label: 'Enseignant / Formateur', emoji: '🧑‍🏫' },
  { value: 'eleve-apprenant', label: 'Élève / Apprenant', emoji: '🎒' },
  { value: 'parent', label: 'Parent', emoji: '👪' },
  { value: 'autre', label: 'Autre', emoji: '👤' },
]

function hasConditionalFields(profileType) {
  return ['enseignant-formateur', 'eleve-apprenant', 'parent'].includes(profileType)
}

export default function ProfilePage() {
  const navigate = useNavigate()
  const [loadingInit, setLoadingInit] = useState(true)
  const [saving, setSaving] = useState(false)
  const [success, setSuccess] = useState(false)
  const [error, setError] = useState('')
  const [userRole, setUserRole] = useState('host')

  const [userEmail, setUserEmail] = useState('')
  const [prenom, setPrenom] = useState('')
  const [nom, setNom] = useState('')
  const [profileType, setProfileType] = useState('')
  const [etablissement, setEtablissement] = useState('')
  const [codePostal, setCodePostal] = useState('')
  const [niveauxSelected, setNiveauxSelected] = useState([])
  const [niveauSingle, setNiveauSingle] = useState('')

  useEffect(() => {
    async function loadProfile() {
      const { data: { session } } = await supabase.auth.getSession()
      if (!session) return

      setUserEmail(session.user.email || '')

      const { data: profile } = await supabase
        .from('profiles')
        .select('*')
        .eq('id', session.user.id)
        .single()

      if (profile) {
        setUserRole(profile.role || 'host')
        setPrenom(profile.prenom || '')
        setNom(profile.nom || '')
        setProfileType(profile.profile_type || '')
        setEtablissement(profile.etablissement || '')
        setCodePostal(profile.code_postal || '')
        if (profile.niveaux) {
          if (profile.profile_type === 'eleve-apprenant') {
            setNiveauSingle(profile.niveaux[0] || '')
          } else {
            setNiveauxSelected(profile.niveaux)
          }
        }
      }
      setLoadingInit(false)
    }
    loadProfile()
  }, [])

  function toggleNiveau(niveau) {
    setNiveauxSelected(prev =>
      prev.includes(niveau) ? prev.filter(n => n !== niveau) : [...prev, niveau]
    )
  }

  async function handleSubmit(e) {
    e.preventDefault()
    setError('')
    setSuccess(false)
    setSaving(true)

    const { data: { session } } = await supabase.auth.getSession()
    if (!session) return

    const updates = {
      prenom: prenom.trim(),
      nom: nom.trim(),
      profile_type: profileType || null,
      etablissement: hasConditionalFields(profileType) ? (etablissement.trim() || null) : null,
      code_postal: hasConditionalFields(profileType) ? (codePostal.trim() || null) : null,
      niveaux: null,
    }

    if (profileType === 'enseignant-formateur') {
      updates.niveaux = niveauxSelected.length > 0 ? niveauxSelected : null
    } else if (profileType === 'eleve-apprenant') {
      updates.niveaux = niveauSingle ? [niveauSingle] : null
    }

    const { error: updateError } = await supabase
      .from('profiles')
      .update(updates)
      .eq('id', session.user.id)

    setSaving(false)
    if (updateError) {
      setError('Erreur lors de la sauvegarde. Réessaie.')
    } else {
      setSuccess(true)
    }
  }

  if (loadingInit) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-900">
        <p className="text-white/60 text-sm">Chargement…</p>
      </div>
    )
  }

  return (
    <div className="min-h-[100dvh] flex flex-col bg-gray-900">

      {/* Header */}
      <header className="bg-gray-900/95 backdrop-blur-sm border-b border-white/10 px-4 py-3 flex items-center justify-between gap-4 shrink-0">
        <div className="flex items-center gap-2">
          <img src="/android-chrome-192x192.png" alt="KartQuiz" className="w-8 h-8 rounded-lg shrink-0" />
          <span className="font-brand text-lg text-white">KartQuiz</span>
          <span className="text-white/40 text-sm hidden sm:block">/ Mon profil</span>
        </div>
        <button
          type="button"
          onClick={() => navigate(-1)}
          className="text-sm text-white/70 hover:text-white hover:bg-white/10 px-3 py-1.5 rounded-lg transition"
        >
          ← Retour
        </button>
      </header>

      <div className="flex-1 flex items-start justify-center p-4 pt-6">
        <div className="w-full max-w-lg">
        <form onSubmit={handleSubmit} className="bg-gray-800 border border-white/10 rounded-2xl p-5 space-y-4">

          {/* Email (lecture seule, vient de auth.users) */}
          {userEmail && (
            <div>
              <label className="block text-sm font-medium text-white/80 mb-1">Adresse email</label>
              <input
                type="text"
                value={userEmail}
                readOnly
                className="w-full border border-white/10 bg-white/20 text-white/60 rounded-lg px-3 py-2 text-sm cursor-not-allowed"
              />
            </div>
          )}

          {/* Identité */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium text-white/80 mb-1">Prénom</label>
              <input
                type="text"
                value={prenom}
                onChange={e => setPrenom(e.target.value)}
                className="w-full border border-white/10 bg-gray-700 text-white rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary placeholder:text-white/30"
                placeholder="Prénom"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-white/80 mb-1">NOM</label>
              <input
                type="text"
                value={nom}
                onChange={e => setNom(e.target.value)}
                className="w-full border border-white/10 bg-gray-700 text-white rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary placeholder:text-white/30"
                placeholder="Nom de famille"
              />
            </div>
          </div>

          {/* Type de profil */}
          <div>
            <label className="block text-sm font-medium text-white/80 mb-2">Je suis…</label>
            <div className="grid grid-cols-2 gap-2">
              {PROFILE_TYPES.map(pt => (
                <button
                  key={pt.value}
                  type="button"
                  onClick={() => setProfileType(pt.value)}
                  className={`flex items-center gap-2 border-2 rounded-xl px-3 py-2.5 text-sm font-medium transition ${
                    profileType === pt.value
                      ? 'border-primary bg-primary/20 text-white'
                      : 'border-white/20 text-white/80 hover:border-white/40'
                  }`}
                >
                  <span className="text-lg">{pt.emoji}</span>
                  <span>{pt.label}</span>
                </button>
              ))}
            </div>
          </div>

          {/* Champs conditionnels */}
          {hasConditionalFields(profileType) && (
            <>
              <div>
                <label className="block text-sm font-medium text-white/80 mb-1">
                  {profileType === 'parent' ? 'Établissement de ton enfant' : 'Établissement'}
                </label>
                <input
                  type="text"
                  value={etablissement}
                  onChange={e => setEtablissement(e.target.value)}
                  className="w-full border border-white/10 bg-gray-700 text-white rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary placeholder:text-white/30"
                  placeholder="Nom de l'établissement (optionnel)"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-white/80 mb-1">Code postal</label>
                <input
                  type="text"
                  value={codePostal}
                  onChange={e => setCodePostal(e.target.value.replace(/\D/g, '').slice(0, 5))}
                  className="w-full border border-white/10 bg-gray-700 text-white rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary placeholder:text-white/30"
                  placeholder="75000 (optionnel)"
                  maxLength={5}
                />
              </div>

              {profileType === 'enseignant-formateur' && (
                <div>
                  <label className="block text-sm font-medium text-white/80 mb-2">Niveaux enseignés</label>
                  <div className="flex flex-wrap gap-1.5">
                    {NIVEAUX.map(n => (
                      <button
                        key={n}
                        type="button"
                        onClick={() => toggleNiveau(n)}
                        className={`px-2.5 py-1 rounded-full text-xs font-medium border transition ${
                          niveauxSelected.includes(n)
                            ? 'bg-primary text-white border-primary'
                            : 'bg-white/10 text-white/70 border-white/20 hover:border-primary'
                        }`}
                      >
                        {n}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {profileType === 'eleve-apprenant' && (
                <div>
                  <label className="block text-sm font-medium text-white/80 mb-1">Niveau scolaire</label>
                  <select
                    value={niveauSingle}
                    onChange={e => setNiveauSingle(e.target.value)}
                    className="w-full border border-white/10 bg-gray-700 text-white rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary placeholder:text-white/30"
                  >
                    <option value="">— Choisir —</option>
                    {NIVEAUX.map(n => <option key={n} value={n}>{n}</option>)}
                  </select>
                </div>
              )}
            </>
          )}

          {error && <p className="text-red-300 text-sm bg-red-900/40 border border-red-500/30 rounded-lg px-3 py-2">{error}</p>}
          {success && <p className="text-green-300 text-sm bg-green-900/40 border border-green-500/30 rounded-lg px-3 py-2">✓ Profil mis à jour.</p>}

          <button
            type="submit"
            disabled={saving}
            className="btn-primary w-full py-2.5 disabled:opacity-60"
          >
            {saving ? '…' : 'Enregistrer'}
          </button>
        </form>
      </div>
    </div>
  </div>
  )
}
